import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ProjectContext } from './Admin/ProjectContext';

const ProjectsPage = () => {
  const { projects, loading, error } = useContext(ProjectContext);
  const [currentPage, setCurrentPage] = useState(1);
  const projectsPerPage = 5;

  // Map database projects to component format
  const formattedProjects = projects.map((project) => ({
    id: project.id,
    title: project.title || 'Untitled Project',
    description:
      project.description?.find((block) => block.type === 'text')?.content ||
      'No description available',
    tech: project.tech_stack || [],
    link: `/projects/${project.id}`,
    logo: project.logo || 'https://upload.wikimedia.org/wikipedia/commons/a/a3/Image-not-found.png',
    logo_background: project.logo_background,
  }));

  const totalPages = Math.ceil(formattedProjects.length / projectsPerPage);
  const currentProjects = formattedProjects.slice(
    (currentPage - 1) * projectsPerPage,
    currentPage * projectsPerPage
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  if (loading) {
    return <p className="text-center text-gray-600 pt-36">Loading projects...</p>;
  }

  if (error) {
    return <p className="text-center text-red-600 pt-36">Error: {error}</p>;
  }

  if (formattedProjects.length === 0) {
    return <p className="text-center text-gray-600 pt-36">No projects found.</p>;
  }

  return (
    <>
      <div className="max-w-7xl pt-36 mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-5xl md:text-8xl font-light text-gray-900 mb-24 text-left">
          Projects
        </h2>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-2">
        <div className="flex flex-col divide-y divide-gray-200">
          {currentProjects.map((project, index) => {
            // displayNumber: (page-1) * projectsPerPage + index + 1
            const displayNumber = (currentPage - 1) * projectsPerPage + index + 1;
            return (
              <Link
                key={project.id}
                to={project.link}
                className="group relative bg-white transition-all duration-1000 ease-in-out hover:z-10 hover:my-4"
              >
                {/* Project Number - Top Right */}
                <span className="absolute top-4 right-8 text-gray-500 text-sm z-10">
                  {String(displayNumber).padStart(2, '0')}
                </span>

                <div className="p-8 grid grid-cols-1 md:grid-cols-[1fr_300px] gap-8 items-center transition-all duration-300">
                  {/* Text Content */}
                  <div className="space-y-4 lg:pr-36">
                    <h3 className="text-3xl font-light text-gray-900 pr-12">
                      {project.title}
                    </h3>

                    <p className="text-gray-600 text-lg font-light line-clamp-2">
                      {project.description}
                    </p>

                    <div className="flex flex-wrap gap-4">
                      {project.tech.map((technology) => (
                        <span
                          key={technology}
                          className="py-1.5 text-gray-600 text-sm font-light"
                        >
                          {technology}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div
                    className="hidden md:block relative h-0 w-96 -translate-x-40 transform transition-all duration-1000 group-hover:h-48 overflow-hidden"
                    style={{ backgroundImage: `url(${project.logo_background})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
                  >
                    {/* Gray tint overlay */}
                    <div className="absolute inset-0 bg-gray-800 opacity-50"></div>
                    {/* Logo */}
                    <img
                      src={project.logo}
                      alt={project.title}
                      className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 object-contain"
                    />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Pagination Controls */}
        <div className="flex justify-center items-center gap-4 py-12">
          <button
            className="p-2 text-gray-600 hover:text-gray-900 disabled:text-gray-300 transition-colors"
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
          >
            <span className="text-2xl font-light">‹</span>
          </button>

          <div className="flex items-center gap-1">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                className={`px-3 py-1 text-sm transition-all font-light
                  ${currentPage === i + 1
                    ? 'text-gray-900 border-b-2 border-gray-900'
                    : 'text-gray-600 hover:text-gray-900 hover:border-b hover:border-gray-300'
                  }`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
          </div>

          <button
            className="p-2 text-gray-600 hover:text-gray-900 disabled:text-gray-300 transition-colors"
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            <span className="text-2xl font-light">›</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default ProjectsPage;